import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './App.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { StorageProvider, useStorage } from './contexts/StorageContext'
import { InstitutionProvider, useInstitution } from './contexts/InstitutionContext'
import InstitutionSwitcher from './components/InstitutionSwitcher'
import PrivateRoute from './components/PrivateRoute'
import RoleBasedRoute from './components/RoleBasedRoute'
import Login from './components/Login'
import Profile from './components/Profile'
import ComingSoonIcon from './assets/coming-soon.svg'
import Sidebar from './components/Sidebar'
import PreviousProjects from './components/PreviousProjects'
import AppNavigation from './components/AppNavigation'
import ModelPreview from './components/ModelPreview'
import { ModelInfo } from './components/ModelPreviewComponents/index'
import ConfirmDialog from './components/ConfirmDialog'
import { StorageQuotaPopup } from './components/StorageQuotaPopup'
import { PricingPopup } from './components/PricingPopup'
import PersistentVideoPlayer from './components/PersistentVideoPlayer'
import { api } from './services/api'
import { auth } from './services/firebase'
import AISupport from './components/AISupport'
import FloatingChatBot from './components/FloatingChatBot'
import { getAnonymousSessionId, isAnonymousUser } from './utils/anonymousSession'

// Import new platform components
import TeacherApp from './platforms/teacher/TeacherApp'
import AdminApp from './platforms/admin/AdminApp'
import InstitutionApp from './platforms/institution/InstitutionApp'

interface UploadedFile {
  id: string;
  original_filename: string;
  original_size_bytes: number;
  detected_mime_type: string;
  upload_time: string;
  status: string;
  project_name?: string;
  glb_path?: string;
  three_mf_path?: string;
  preview_url?: string;
  urls?: {
    original?: string;
    glb?: string;
    three_mf?: string;
  };
}

// Main content component that uses auth
function AuthenticatedApp() {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<string>('Loading...')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [projectName, setProjectName] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({})
  const [overallProgress, setOverallProgress] = useState(0)
  const [processingStep, setProcessingStep] = useState<string>('')
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [currentProject, setCurrentProject] = useState<string | null>(null)
  const [projectModels, setProjectModels] = useState<ModelInfo[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [showPreviewMode, setShowPreviewMode] = useState(false)
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<string | null>(null)
  const [showStorageQuotaPopup, setShowStorageQuotaPopup] = useState(false)
  const [showPricingPopup, setShowPricingPopup] = useState(false)
  const [storageQuotaFailedFiles, setStorageQuotaFailedFiles] = useState(0)
  const [showInsufficientStoragePopup, setShowInsufficientStoragePopup] = useState(false)
  const [insufficientStorageData, setInsufficientStorageData] = useState<{
    needed: string;
    available: string;
    shortfall: string;
    filesCount: number;
  } | undefined>(undefined)
  
  // Get the current authenticated user
  const { currentUser, logout } = useAuth();
  const { institution, theme, applyTheme } = useInstitution();
  const navigate = useNavigate();

  // Apply institution theming
  useEffect(() => {
    applyTheme();
  }, [theme, applyTheme]);

  // Role-based redirection logic
  useEffect(() => {
    const checkUserRoleAndRedirect = async () => {
      if (currentUser) {
        try {
          const roleData = await api.auth.getRole();
          console.log('User role data:', roleData);
          
          // Redirect based on role
          switch (roleData.role) {
            case 'teacher':
              console.log('Redirecting to teacher dashboard...');
              navigate('/teacher/dashboard');
              break;
            case 'super_admin':
              console.log('Redirecting to admin dashboard...');
              navigate('/admin/dashboard');
              break;
            case 'institution_admin':
              console.log('Redirecting to institution dashboard...');
              navigate('/institution/dashboard');
              break;
            case 'student':
            default:
              // Stay on current platform - this is the student platform
              console.log('User is student, staying on current platform');
              break;
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          // If role check fails, assume student and stay here
        }
      }
    };

    // Only check role once when component mounts and user is available
    if (currentUser) {
      checkUserRoleAndRedirect();
    }
  }, [currentUser, navigate]); // Only depend on currentUser and navigate

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await api.health.check()
        setStatus(t('messages.connectedToBackend'))
        
        // Only load files if user is authenticated
        if (currentUser) {
          loadFiles()
        } else {
          console.log("User not logged in - skipping file loading")
        }
      } catch (error) {
        setStatus(t('messages.errorConnecting'))
        console.error('Health check failed:', error)
      }
    }

    const loadFiles = async () => {
      // Only load files for authenticated users
      if (currentUser) {
        try {
          const response = await api.files.list()
          setFiles(response.files)
        } catch (error) {
          console.error('Error loading files:', error)
        }
      } else {
        console.log('[DEBUG] Skipping file list load for anonymous user')
      }
    }

    checkHealth()
  }, [currentUser, t]) // Re-run when currentUser changes

  // Also add a separate effect to load files when user logs in
  useEffect(() => {
    const loadFilesAfterLogin = async () => {
      if (currentUser) {
        try {
          const response = await api.files.list()
          setFiles(response.files)
        } catch (error) {
          console.error('Error loading files after login:', error)
        }
      }
    }
    
    loadFilesAfterLogin()
  }, [currentUser])

  const loadProjectModels = async (projectName: string) => {
    // Allow both authenticated and anonymous users to view projects
    // Anonymous users can view their own projects created in the current session
    
    setLoadingModels(true)
    setCurrentProject(projectName)
    setProjectModels([])
    setShowPreviewMode(true)
    
    try {
      console.log(`[DEBUG] Loading models for project: ${projectName}`);
      const response = await api.files.getFilesByProject(projectName)
      console.log(`[DEBUG] Got ${response.files.length} files for project ${projectName}`);
      
      // Convert files to ModelInfo format
      const modelInfos: ModelInfo[] = []
      
      for (const file of response.files) {
        console.log(`[DEBUG] Processing file: ${file.id}, status: ${file.status}, has glb_path: ${!!file.glb_path}`);
        if ((file.status === 'processed' || file.status === 'uploaded' || file.status === 'auto_oriented') && file.glb_path) {
          try {
            console.log(`[DEBUG] Getting GLB URL for file: ${file.id}, path: ${file.glb_path}`);
            // Always get a fresh URL for the GLB file to avoid expiration issues
            const glbUrl = await api.files.getDownloadUrl(file.id, 'glb')
            console.log(`[DEBUG] Successfully got GLB URL for file: ${file.id}`);
            
            modelInfos.push({
              id: file.id,
              filename: file.original_filename,
              size: file.original_size_bytes || 0,
              status: file.status,
              glbUrl: glbUrl,
              originalUrl: (file as any).urls?.original || '',
            })
          } catch (err) {
            console.error(`[DEBUG] Error getting URLs for file ${file.id}:`, err)
          }
        } else {
          console.log(`[DEBUG] Skipping file ${file.id}: status=${file.status}, has glb_path=${!!file.glb_path}`);
        }
      }
      
      console.log(`[DEBUG] Final modelInfos: ${modelInfos.length} models`);
      setProjectModels(modelInfos)
    } catch (error) {
      console.error('[DEBUG] Error loading project models:', error)
      setError('Failed to load project models')
    } finally {
      setLoadingModels(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setError('')
    
    // Check each file for size limit (100MB = 100 * 1024 * 1024 bytes)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];
    
    console.log(`[DEBUG] Processing ${files.length} selected files for size validation`);
    
    Array.from(files).forEach(file => {
      const fileSizeMB = file.size / (1024 * 1024);
      console.log(`[DEBUG] File: ${file.name}, Size: ${fileSizeMB.toFixed(2)} MB`);
      
      if (file.size > MAX_FILE_SIZE) {
        console.log(`[DEBUG] File exceeds size limit: ${file.name}`);
        oversizedFiles.push(`${file.name} (${fileSizeMB.toFixed(1)} MB)`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (oversizedFiles.length > 0) {
      const errorMessage = `The following files exceed the 100MB limit and will not be uploaded: ${oversizedFiles.join(', ')}`;
      console.error(errorMessage);
      setError(errorMessage);
      
      // Alert the user as well to make sure they see the message
      setTimeout(() => {
        alert(`Files over 100MB cannot be uploaded:\n\n${oversizedFiles.join('\n')}`);
      }, 100);
    }
    
    console.log(`[DEBUG] Proceeding with ${validFiles.length} valid files`);
    setSelectedFiles(prev => [...prev, ...validFiles]);
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  // Drag and drop handlers
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const event = { target: { files } } as any
      handleFileSelect(event)
    }
  }

  const handleFileAreaClick = () => {
    document.getElementById('fileUpload')?.click()
  }

  const handleProcess = async () => {
    // Set loading state immediately for instant user feedback
    setIsProcessing(true)
    setError('')
    // Initialize progress tracking
    const initialProgress = Object.fromEntries(
      selectedFiles.map(file => [file.name, 0])
    )
    setUploadProgress(initialProgress)
    setOverallProgress(0)
    setProcessingStep('Uploading files')
    setCurrentStepIndex(0)

    if (selectedFiles.length === 0) {
      setError('No files selected for processing')
      setIsProcessing(false)
      return
    }

    if (!projectName.trim()) {
      setError('Please enter a project name')
      setIsProcessing(false)
      return
    }

    // Note: Anonymous users are now allowed to upload and generate G-code
    // Authentication is only required when trying to download files

    // Pre-upload storage validation - check if we have enough space for ALL files
    // Skip for anonymous users since their files expire after 1 hour
    if (currentUser) {
      try {
        console.log('[DEBUG] Starting pre-upload storage validation for authenticated user...')
        
        // Get current storage usage
        const storageResponse = await api.storage.getUsage()
        if (!storageResponse.success || !storageResponse.quota) {
          console.warn('[DEBUG] Could not get storage usage, proceeding with upload (will be caught by backend)')
        } else {
          // Calculate total estimated storage needed for all selected files
          // Using same 2.5× multiplier as backend (original + 3MF + GLB)
          const totalOriginalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0)
          const totalEstimatedSize = totalOriginalSize * 2.5
          
          const availableStorage = storageResponse.quota.available_bytes
          
          console.log(`[DEBUG] Storage check: Need ${(totalEstimatedSize/1024/1024).toFixed(1)}MB, Available ${(availableStorage/1024/1024).toFixed(1)}MB`)
          
          if (totalEstimatedSize > availableStorage) {
            const neededMB = (totalEstimatedSize / 1024 / 1024).toFixed(1)
            const availableMB = (availableStorage / 1024 / 1024).toFixed(1)
            const shortfallMB = ((totalEstimatedSize - availableStorage) / 1024 / 1024).toFixed(1)
            
            setInsufficientStorageData({
              needed: neededMB,
              available: availableMB,
              shortfall: shortfallMB,
              filesCount: selectedFiles.length
            })
            setShowInsufficientStoragePopup(true)
            setIsProcessing(false)
            return // Block upload entirely
          }
          
          console.log('[DEBUG] Storage validation passed, proceeding with upload')
        }
      } catch (storageError) {
        console.warn('[DEBUG] Storage validation failed, proceeding anyway:', storageError)
        // Don't block upload if storage check fails - let backend handle it
      }
    } else {
      console.log('[DEBUG] Skipping storage validation for anonymous user (files expire after 1 hour)')
    }
    
    try {
      // Calculate total expected work units
      // Upload (50% of progress) + Processing (50% of progress)
      const totalWorkUnits = selectedFiles.length * 2
      let completedWorkUnits = 0
      
      // Create an array to track upload promises
      const uploadPromises = selectedFiles.map(async (file) => {
        try {
          // Create XMLHttpRequest to track upload progress
          const xhr = new XMLHttpRequest()
          const formData = new FormData()
          formData.append('file', file)
          formData.append('project_name', projectName)
          
          // Create a promise that will resolve when the upload is complete
          const uploadPromise = new Promise<any>((resolve, reject) => {
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                // Calculate progress percentage for this file
                const progress = Math.round((event.loaded / event.total) * 100)
                
                // Update progress for this specific file
                setUploadProgress(prev => {
                  const newProgress = { ...prev, [file.name]: progress }
                  
                  // Calculate total progress across all files for upload phase (50% of total)
                  // 50% of the progress bar is for uploads, 50% is for processing
                  const uploadProgressAvg = Object.values(newProgress).reduce((sum, value) => sum + value, 0) / selectedFiles.length
                  const totalProgress = Math.round(uploadProgressAvg * 0.5) // Upload is 50% of total process
                  setOverallProgress(totalProgress)
                  
                  return newProgress
                })
              }
            })
            
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText)
                  
                  // Update work units - file upload completed
                  completedWorkUnits += 1
                  const percentComplete = Math.round((completedWorkUnits / totalWorkUnits) * 100)
                  setOverallProgress(percentComplete > 50 ? percentComplete : 50) // Ensure we show at least 50%
                  
                  resolve(response)
                } catch (e) {
                  reject(new Error('Invalid response from server'))
                }
              } else {
                try {
                  const errorData = JSON.parse(xhr.responseText)
                  const errorMessage = errorData.detail || 'Upload failed';
                  
                  // Check if this is a storage quota exceeded error
                  if (errorMessage.includes('Storage quota exceeded') || 
                      errorMessage.includes('storage limit') ||
                      xhr.status === 413) {
                    reject(new Error('STORAGE_QUOTA_EXCEEDED'))
                  } else {
                    reject(new Error(errorMessage))
                  }
                } catch (e) {
                  reject(new Error(`Upload failed with status ${xhr.status}`))
                }
              }
            })
            
            xhr.addEventListener('error', () => {
              reject(new Error('Network error during upload'))
            })
            
            xhr.addEventListener('abort', () => {
              reject(new Error('Upload aborted'))
            })
          })
          
          // Handle authentication for both logged-in and anonymous users
          const token = await auth.currentUser?.getIdToken()
          
          // For anonymous users, add session ID to form data
          if (!currentUser) {
            const anonymousSessionId = getAnonymousSessionId();
            formData.append('anonymous_session_id', anonymousSessionId);
            console.log('[DEBUG] Using anonymous session ID:', anonymousSessionId);
          }
          
          // Fix for the upload URL - use direct server URLs
          const hostname = window.location.hostname;
          console.log('[DEBUG] Current hostname:', hostname);
          
          // Hardcode the correct URL based on hostname
          let uploadUrl = '';
          if (hostname === 'localhost') {
            uploadUrl = 'http://localhost:8001/api/v1/files/upload';
          } else if (hostname === 'staging.maxel.ai') {
            uploadUrl = '/api/v1/files/upload';
          } else if (hostname === 'app.maxel.ai') {
            uploadUrl = '/api/v1/files/upload';
          } else {
            // Default fallback
            uploadUrl = '/api/v1/files/upload';
          }
          
          console.log('[DEBUG] Using upload URL:', uploadUrl);
          
          xhr.open('POST', uploadUrl);
          
          // Only set auth header for logged-in users
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`)
          }
          
          // Start the upload
          xhr.send(formData)
          
          return await uploadPromise
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error)
          throw error
        }
      })

      const results = await Promise.allSettled(uploadPromises)
      
      // Filter successful uploads
      const successfulUploads = results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map(result => result.value)
      
      // Update files list with new uploads
      setFiles(prev => [...prev, ...successfulUploads])
      
      // Clear selected files and project name after successful upload
      setSelectedFiles([])
      
      // Don't reset progress yet - we'll continue tracking backend processing
      // setUploadProgress({})
      // setOverallProgress(0)

      // Check for any failures
      const failures = results.filter(result => result.status === 'rejected')
      if (failures.length > 0) {
        // Check if any failures are due to storage quota exceeded
        const storageQuotaFailures = failures.filter(failure => 
          failure.reason && failure.reason.message === 'STORAGE_QUOTA_EXCEEDED'
        );
        
        if (storageQuotaFailures.length > 0) {
          // Storage quota exceeded - show popup instead of generic error
          setStorageQuotaFailedFiles(storageQuotaFailures.length);
          setShowStorageQuotaPopup(true);
          setIsProcessing(false);
          
          // If some files succeeded, update the file list
          if (successfulUploads.length > 0) {
            setFiles(prev => [...prev, ...successfulUploads]);
          }
          
          return;
        } else {
          // Other types of failures - show generic error
          setError(`Failed to upload ${failures.length} file(s). Check console for details.`)
          setIsProcessing(false)
          return
        }
      }

      // Set progress to 50% explicitly - uploads are done
      setOverallProgress(50)
      
      // Process each file in sequence
      const fileIds = successfulUploads.map(file => file.id)
      
      // Update processing step messages during subsequent operations
      setProcessingStep('Detecting file dimensions')
      setCurrentStepIndex(1)
      await new Promise(resolve => setTimeout(resolve, 1000))
      setOverallProgress(60)
      
      setProcessingStep('Finding best print orientation')
      setCurrentStepIndex(2)
      await new Promise(resolve => setTimeout(resolve, 1000))
      setOverallProgress(70)
      
      for (let i = 0; i < fileIds.length; i++) {
        const fileId = fileIds[i]
        try {
          await api.slicing.process(fileId)
          console.log(`[DEBUG] Successfully processed file ${i + 1}/${fileIds.length}: ${fileId}`)
        } catch (fileError) {
          console.error(`[DEBUG] Error processing file ${i + 1}/${fileIds.length} (${fileId}):`, fileError)
          // Continue with other files even if this one fails
        }
        
        // Update progress as each file completes processing (successful or failed)
        completedWorkUnits += 1
        const percentComplete = Math.round((completedWorkUnits / totalWorkUnits) * 100)
        setOverallProgress(percentComplete)
      }
      
      setProcessingStep('Generating visual representation')
      setCurrentStepIndex(3)
      await new Promise(resolve => setTimeout(resolve, 1000))
      setOverallProgress(90)
      
      // Refresh file list after processing (skip for anonymous users as they don't need the full file list)
      if (currentUser) {
        try {
          const response = await api.files.list()
          setFiles(response.files)
        } catch (error) {
          console.warn('[DEBUG] Could not refresh file list, continuing anyway:', error)
          // Don't fail the entire process if file listing fails
        }
      } else {
        console.log('[DEBUG] Skipping file list refresh for anonymous user')
      }
      
      // Load the processed models for this project
      await loadProjectModels(projectName)
      
      // All done - set progress to 100%
      setOverallProgress(100)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Processing failed')
      console.error('Processing error:', error)
    } finally {
      setIsProcessing(false)
      setProcessingStep('')
      setCurrentStepIndex(0)
      setUploadProgress({})
    }
  }

  const handleBackToUpload = () => {
    setShowPreviewMode(false)
    setCurrentProject(null)
    setProjectModels([])
  }

  // Handler for initiating project deletion (shows confirmation)
  const handleDeleteProjectConfirm = (projectName: string) => {
    setDeleteConfirmProject(projectName)
  }

  // Handler for confirmed project deletion
  const handleDeleteProject = () => {
    if (deleteConfirmProject) {
      // Update the files list by removing all files associated with this project
      setFiles(prevFiles => prevFiles.filter(file => file.project_name !== deleteConfirmProject))
      
      // Close the confirmation dialog
      setDeleteConfirmProject(null)
    }
  }

  // Cancel project deletion
  const handleCancelDelete = () => {
    setDeleteConfirmProject(null)
  }
  
  // Regular content for all users
  return (
    <div className="app-container">
      <Sidebar currentUser={currentUser} />
      
      <div className={`main-content with-sidebar`}>
        <AppNavigation />
        <div className="App">
          {error && error !== 'Please enter a project name' && (
            <div className={`error-message ${error.includes('storage') || error.includes('Storage') ? 'storage-error' : ''}`}>
              <p>{error}</p>
              {(error.includes('storage') || error.includes('Storage')) && (
                <div className="error-suggestions">
                  <p><strong>What you can do:</strong></p>
                  <ul>
                    <li>Delete some existing files from your projects</li>
                    <li>Upload fewer files at once</li>
                    <li>Upgrade your plan for more storage space</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {showPreviewMode ? (
            // Preview Mode UI
            <div className="preview-mode">
              <div className="preview-header">
                <button 
                  className="back-button"
                  onClick={handleBackToUpload}
                >
                  &larr; {t('navigation.backToUpload')}
                </button>
              </div>
              
              {currentProject && (
                <ModelPreview 
                  projectName={currentProject} 
                  models={projectModels}
                  loading={loadingModels}
                />
              )}
            </div>
          ) : (
            // Upload Mode UI
            <>
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                minHeight: "calc(100vh - 200px)" 
              }}>
                <div className="upload-form">
                  <h1>{t('uploadPage.headline')}</h1>
                  <p>
                    {t('uploadPage.description')}
                  </p>
                  
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div className="form-group">
                      <label htmlFor="projectName">{t('uploadPage.projectNameLabel')}</label>
                      <div className="project-input-container">
                        <input
                          type="text"
                          id="projectName"
                          value={projectName}
                          onChange={(e) => setProjectName(e.target.value)}
                          placeholder={t('uploadPage.projectNamePlaceholder')}
                          className="project-name-input"
                        />
                      </div>
                    </div>
                    
                    {!isProcessing && (
                      <div className="form-group">
                        <div 
                          className={`file-drop-area ${isDragOver ? 'drag-over' : ''}`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={handleFileAreaClick}
                        >
                          <div className="file-icon">
                            <img src="/file_icon.png" alt="File icon" width="55" height="auto" />
                          </div>
                          <div className="drag-text">{t('uploadPage.dragDropText')}</div>
                          <div className="size-limit-text">{t('uploadPage.fileSizeWarning')}</div>
                          <input
                            id="fileUpload"
                            type="file"
                            accept=".stl,.obj,.3mf"
                            onChange={handleFileSelect}
                            multiple
                            className="file-input-hidden"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {selectedFiles.length > 0 && !isProcessing && (
                    <div className="uploaded-files">
                      <div className="uploaded-files-title">{t('uploadPage.uploadedFilesTitle')}</div>
                      <div className="files-container">
                        <ul>
                          {selectedFiles.map((file, index) => (
                            <li key={index} className="uploaded-file-item">
                              <div className="file-info">
                                <div className="file-name">{file.name}</div>
                                <div className="file-size">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                              </div>
                              <button 
                                className="remove-file-btn"
                                onClick={() => handleRemoveFile(index)}
                                aria-label={`Remove ${file.name}`}
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="storage-usage-info">
                        <div className="storage-breakdown">
                          {(() => {
                            const totalOriginalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
                            const totalEstimatedSize = totalOriginalSize * 2.5;
                            
                            return (
                              <>
                                <div className="storage-line">
                                  <span className="storage-label">{t('uploadPage.storageLabels.yourFiles')}</span>
                                  <span className="storage-value">{(totalOriginalSize / 1024 / 1024).toFixed(1)} MB</span>
                                </div>
                                <div className="storage-line">
                                  <span className="storage-label">{t('uploadPage.storageLabels.processingFiles')}</span>
                                  <span className="storage-value">{((totalEstimatedSize - totalOriginalSize) / 1024 / 1024).toFixed(1)} MB</span>
                                </div>
                                <div className="storage-line total">
                                  <span className="storage-label">{t('uploadPage.storageLabels.totalStorage')}</span>
                                  <span className="storage-value">{(totalEstimatedSize / 1024 / 1024).toFixed(1)} MB</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="file-action-buttons">
                        <button 
                          className="cancel-btn"
                          onClick={() => setSelectedFiles([])}
                        >
                          {t('buttons.cancel')}
                        </button>
                        <button 
                          className="attach-files-btn"
                          onClick={handleProcess} 
                          disabled={isProcessing}
                        >
                          {isProcessing ? t('buttons.processing') : t('buttons.attachFiles')}
                        </button>
                      </div>
                      
                      {error === 'Please enter a project name' && (
                        <div className="inline-error-message">
                          {t('messages.pleaseEnterProjectName')}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isProcessing && (
                    <div className="loading-bar-container">
                      <div className="loading-segments">
                        {[
                          t('uploadPage.processingSteps.uploadingFiles'),
                          t('uploadPage.processingSteps.detectingDimensions'),
                          t('uploadPage.processingSteps.findingOrientation'),
                          t('uploadPage.processingSteps.generatingPreview')
                        ].map((step, index) => (
                          <div 
                            key={index}
                            className={`loading-bar-segment ${
                              index < currentStepIndex ? 'active' : 
                              index === currentStepIndex ? 'loading' : ''
                            }`}
                          ></div>
                        ))}
                      </div>
                      <div className="loading-step-labels">
                        {[
                          { title: t('uploadPage.processingSteps.uploadingFiles') },
                          { title: t('uploadPage.processingSteps.detectingDimensions') },
                          { title: t('uploadPage.processingSteps.findingOrientation') },
                          { title: t('uploadPage.processingSteps.generatingPreview') }
                        ].map((step, index) => (
                          <div 
                            key={index}
                            className={`loading-step-label ${
                              index < currentStepIndex ? 'completed' : 
                              index === currentStepIndex ? 'active' : 'waiting'
                            }`}
                          >
                            <div className="step-title">{step.title}</div>
                            <div className="step-status">
                              {index < currentStepIndex ? t('uploadPage.processingStatus.completed') : 
                               index === currentStepIndex ? t('uploadPage.processingStatus.processing') : t('uploadPage.processingStatus.waiting')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        <p className="status-indicator">
          <span className="status-indicator-dot"></span>
          {status}
        </p>
        
        {/* Confirmation Dialog for Project Deletion */}
        <ConfirmDialog
          isOpen={deleteConfirmProject !== null}
          title="Delete Project"
          message={`Are you sure you want to delete the project "${deleteConfirmProject}"? This cannot be undone.`}
          onConfirm={handleDeleteProject}
          onCancel={handleCancelDelete}
          confirmText="Delete"
        />
        
        {/* Storage Quota Exceeded Popup */}
        <StorageQuotaPopup
          isOpen={showStorageQuotaPopup}
          onClose={() => {
            setShowStorageQuotaPopup(false);
            setStorageQuotaFailedFiles(0);
          }}
          onUpgrade={() => {
            setShowPricingPopup(true);
          }}
          filesCount={storageQuotaFailedFiles}
        />
        
        {/* Insufficient Storage Popup */}
        <StorageQuotaPopup
          isOpen={showInsufficientStoragePopup}
          type="insufficient_storage"
          storageData={insufficientStorageData}
          onClose={() => {
            setShowInsufficientStoragePopup(false);
            setInsufficientStorageData(undefined);
          }}
          onUpgrade={() => {
            setShowPricingPopup(true);
          }}
        />
        
        {/* Pricing Popup */}
        <PricingPopup
          isOpen={showPricingPopup}
          onClose={() => setShowPricingPopup(false)}
          onPlanChange={() => {
            // Refresh the page or reload data after plan change
            window.location.reload();
          }}
        />
      </div>
    </div>
  );
}

// Simple App component that provides the AuthContext
function App() {
  // Global persistent video player state
  const [showVideoPlayer, setShowVideoPlayer] = useState(true); // Start with true by default

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
  };

  const handleShowVideoPlayer = () => {
    setShowVideoPlayer(true);
  };

  return (
    <Router>
      <InstitutionProvider>
      <AuthProvider>
        <StorageProvider>
          <VideoPlayerManager 
            showVideoPlayer={showVideoPlayer}
            setShowVideoPlayer={setShowVideoPlayer}
            onClose={handleCloseVideoPlayer}
            onShow={handleShowVideoPlayer}
          />
          <Routes>
            <Route path="/login" element={<Login />} />
            
            {/* New Platform Routes */}
            <Route path="/teacher/*" element={
              <RoleBasedRoute allowedRoles={['teacher', 'institution_admin', 'super_admin']}>
                <TeacherApp />
              </RoleBasedRoute>
            } />
            <Route path="/admin/*" element={
              <RoleBasedRoute allowedRoles={['super_admin']}>
                <AdminApp />
              </RoleBasedRoute>
            } />
            <Route path="/institution/*" element={
              <RoleBasedRoute allowedRoles={['institution_admin', 'super_admin']}>
                <InstitutionApp />
              </RoleBasedRoute>
            } />
            
            {/* Existing Student Platform Routes */}
            <Route path="/profile" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            <Route path="/" element={<AuthenticatedApp />} />
            <Route path="/projects" element={
              <PrivateRoute>
                <PreviousProjects />
              </PrivateRoute>
            } />
            <Route path="/ai-support" element={
              <PrivateRoute>
                <AISupport />
              </PrivateRoute>
            } />
            <Route path="/quotation" element={
              <PrivateRoute>
                <ComingSoon title="Automatic Quotation" />
              </PrivateRoute>
            } />
            <Route path="/queue" element={
              <PrivateRoute>
                <ComingSoon title="G-Code Queue" />
              </PrivateRoute>
            } />
            <Route path="/history" element={
              <PrivateRoute>
                <ComingSoon title="3D Print History" />
              </PrivateRoute>
            } />
            <Route path="/statistics" element={
              <PrivateRoute>
                <ComingSoon title="Statistics" />
              </PrivateRoute>
            } />
            <Route path="/materials" element={
              <PrivateRoute>
                <ComingSoon title="Prints and Materials" />
              </PrivateRoute>
            } />
            <Route path="/settings" element={
              <PrivateRoute>
                <ComingSoon title="Settings" />
              </PrivateRoute>
            } />
          </Routes>
          {/* Global Floating Chat Bot - Available on all pages */}
          <FloatingChatBot />
          {/* Institution Debug Panel - Development only */}
          <InstitutionSwitcher />
        </StorageProvider>
      </AuthProvider>
    </InstitutionProvider>
    </Router>
  )
}

// Video Player Manager Component that handles user-specific logic
function VideoPlayerManager({ 
  showVideoPlayer, 
  setShowVideoPlayer, 
  onClose, 
  onShow 
}: {
  showVideoPlayer: boolean;
  setShowVideoPlayer: (show: boolean) => void;
  onClose: () => void;
  onShow: () => void;
}) {
  const { currentUser } = useAuth();

  useEffect(() => {
    // If user is not logged in, always show the video (for new potential users)
    if (!currentUser) {
      setShowVideoPlayer(true);
      return;
    }

    // If user is logged in, check their personal preference
    const userKey = `videoPlayerClosed_${currentUser.uid}`;
    const userHasClosedVideo = localStorage.getItem(userKey) === 'true';
    
    // Show video unless this specific user has closed it before
    setShowVideoPlayer(!userHasClosedVideo);
  }, [currentUser, setShowVideoPlayer]);

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
    
    // Only save preference if user is logged in
    if (currentUser) {
      const userKey = `videoPlayerClosed_${currentUser.uid}`;
      localStorage.setItem(userKey, 'true');
    }
    
    onClose();
  };

  const handleShowVideoPlayer = () => {
    setShowVideoPlayer(true);
    
    // Remove the closed preference if user is logged in
    if (currentUser) {
      const userKey = `videoPlayerClosed_${currentUser.uid}`;
      localStorage.removeItem(userKey);
    }
    
    onShow();
  };

  return (
    <PersistentVideoPlayer
      videoUrl="https://video.gumlet.io/67c74e8e0090d97ecfc91aca/683899e871151e6b584cc539/download.mp4"
      isVisible={showVideoPlayer}
      onClose={handleCloseVideoPlayer}
      onShow={handleShowVideoPlayer}
    />
  );
}

// Simple placeholder for features coming soon
function ComingSoon({ title }: { title: string }) {
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  return (
    <div className="app-container">
      <Sidebar currentUser={currentUser} />
      
      <div className={`main-content with-sidebar`}>
        <AppNavigation />
        <div className="coming-soon">
          <h1>{title}</h1>
          <p>{t('comingSoon.message')}</p>
          <button onClick={() => navigate('/')}>{t('comingSoon.backToHome')}</button>
        </div>
      </div>
    </div>
  );
}

export default App
